import { useEffect, useRef, useState } from "react";
import type { PhotoItem } from "../domain/project";
import { readAssetFile } from "../storage/asset-store";

function getUniqueAssetIds(items: PhotoItem[]) {
  return [...new Set(items.map((item) => item.assetId))];
}

export function getAssetMembershipKey(items: PhotoItem[]) {
  return JSON.stringify(getUniqueAssetIds(items).sort());
}

export function diffAssetIds(current: Iterable<string>, desired: Iterable<string>) {
  const currentSet = new Set(current);
  const desiredSet = new Set(desired);
  return {
    added: [...desiredSet].filter((assetId) => !currentSet.has(assetId)),
    removed: [...currentSet].filter((assetId) => !desiredSet.has(assetId)),
  };
}

export function useAssetUrls(projectId: string, items: PhotoItem[], variant: "preview" | "thumbnail") {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const cacheRef = useRef(new Map<string, string>());
  const generationRef = useRef(0);
  const membershipKey = getAssetMembershipKey(items);

  useEffect(() => {
    generationRef.current += 1;
    for (const url of cacheRef.current.values()) URL.revokeObjectURL(url);
    cacheRef.current.clear();
    setUrls({});

    return () => {
      generationRef.current += 1;
      for (const url of cacheRef.current.values()) URL.revokeObjectURL(url);
      cacheRef.current.clear();
    };
  }, [projectId, variant]);

  useEffect(() => {
    let active = true;
    const generation = ++generationRef.current;
    const desiredIds = getUniqueAssetIds(items);
    const { added, removed } = diffAssetIds(cacheRef.current.keys(), desiredIds);

    for (const assetId of removed) {
      const url = cacheRef.current.get(assetId);
      if (url) URL.revokeObjectURL(url);
      cacheRef.current.delete(assetId);
    }
    setUrls(Object.fromEntries(cacheRef.current));

    Promise.allSettled(added.map(async (assetId) => {
      const blob = await readAssetFile(projectId, assetId, variant);
      if (!active || generation !== generationRef.current) return null;
      const url = URL.createObjectURL(blob);
      cacheRef.current.set(assetId, url);
      return [assetId, url] as const;
    })).then(() => {
      if (!active || generation !== generationRef.current) return;
      setUrls(Object.fromEntries(cacheRef.current));
    });

    return () => {
      active = false;
    };
  }, [projectId, variant, membershipKey]);

  return urls;
}
